import { HttpStatusCode } from 'axios';
import { useSetAtom } from 'jotai';
import React, { PropsWithChildren, useEffect, useState } from 'react';

import {
  OnboardingDetailsDtoOnboardingStatusEnum,
  UserDetailsWithPermissionDto,
} from '@/api/generated';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp.tsx';
import { toUserContext, userAtom } from '@/contexts/userContext/User.context.ts';
import { isApprovedUser } from '@/contexts/userContext/util/isApprovedUser.util.ts';
import { useGetCurrentUser } from '@/contexts/userContext/util/useGetCurrentUser.util.ts';
import { useGetUserOnboardingDetails } from '@/contexts/userContext/util/useGetUserOnboardingDetails.util.ts';
import { useOnboardUser } from '@/contexts/userContext/util/useOnboardUser.util.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { returnPromise } from '@/utils/returnPromise.util.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { Forbidden } from '@/views/generalError/forbidden/Forbidden.comp.tsx';
import { GeneralError } from '@/views/generalError/GeneralError.view.tsx';
import NotAuthorized from '@/views/generalError/notAuthorized/NotAuthorized.view.tsx';
import WaitingForApproval from '@/views/generalError/waitingForApproval/WaitingForApproval.view.tsx';

const PATHS_WITHOUT_SESSION = [RouteEnum.NOT_AUTHORIZED, RouteEnum.WAITING_FOR_APPROVAL];

export const UserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslationWithPrefix('utils.userContext');
  const [isInitialized, setIsInitialized] = useState(false);
  const setUser = useSetAtom(userAtom);
  const { location } = useAppNavigation();
  const [onboardingInProgress, setOnboardingInProgress] = useState(false);
  const [notApproved, setNotApproved] = useState(false);
  const pathWithoutSession = PATHS_WITHOUT_SESSION.map(path => path.toString()).includes(
    location.pathname,
  );

  const setUserOnSuccess = (newUser: UserDetailsWithPermissionDto) => {
    setUser(toUserContext(newUser));
  };
  const { errorGettingUser, getCurrentUser, isGettingCurrentUser } = useGetCurrentUser();
  const { getOnboardingDetails } = useGetUserOnboardingDetails();
  const { onboardUser } = useOnboardUser();

  const init = async () => {
    if (pathWithoutSession) {
      setIsInitialized(true);
      setUser(cur => ({
        ...cur,
        dataReady: true,
      }));
      return;
    }

    const { data } = await getCurrentUser();
    if (!data) {
      throw new Error('User not found');
    }

    if (!isApprovedUser(data)) {
      setNotApproved(true);
      setIsInitialized(true);
      return;
    }

    setUserOnSuccess(data);
    let onboardingDetails = await getOnboardingDetails(data.userId);
    if (onboardingDetails.onboardingStatus === OnboardingDetailsDtoOnboardingStatusEnum.Completed) {
      setIsInitialized(true);
      return;
    }
    setOnboardingInProgress(true);
    await onboardUser(data.userId);

    while (
      onboardingDetails.onboardingStatus !== OnboardingDetailsDtoOnboardingStatusEnum.Completed
    ) {
      await returnPromise(5000);
      onboardingDetails = await getOnboardingDetails(data.userId);
    }

    setOnboardingInProgress(false);
    setIsInitialized(true);
  };

  useEffect(() => {
    if (window.location.pathname === '/logout') {
      return;
    }

    void init();
  }, []);

  if (errorGettingUser?.response?.status === HttpStatusCode.Forbidden) {
    return <Forbidden />;
  }

  if (errorGettingUser?.response?.status === HttpStatusCode.Unauthorized) {
    return <NotAuthorized />;
  }

  if (errorGettingUser) {
    return <GeneralError hideButton />;
  }

  if (isGettingCurrentUser) {
    return <FullPageSpinner />;
  }

  if (notApproved) {
    return <WaitingForApproval />;
  }

  if (onboardingInProgress) {
    return <FullPageSpinner caption={t('onboarding')} />;
  }

  if (!isInitialized) {
    return <FullPageSpinner />;
  }

  return <React.Fragment>{children}</React.Fragment>;
};
